import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useForm from 'react-hook-form';
import {
  TabContent,
  TabPane,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  CustomInput,
} from 'reactstrap';
import {
  ContFlexRight,
  ButtonSave,
  ButtonDelete,
  H5Styled,
  BtnRadiusRed,
  InputLink,
  ContFilterCheckbox,
} from '../../../styled-components';
import ModalConfirm from '../../../commons/ModalConfirm';
import regions from '../../../lib/helpers/regions';
import { useAlert } from 'react-alert';
import { URL_REGEX } from '../../../lib/helpers/url';
import { AdminTypes, ProfileTypes } from '../../../lib/constants';
import { useAuth } from '../../../commons/Auth';
import FormErrorMessage from '../../../commons/FormErrorMessage';
import { ADMIN_TYPES, AGENT_TYPES } from '../../../App';
import {
  FISCAL_CODE_REGEX,
  POSTAL_CODE_REGEX,
  VAT_CODE_REGEX,
} from '../../../lib/regex';
import { validateFiscalCode } from '../../../lib/fetch/profiles';
import { refactoringAdminLevelsAddRegistry } from '../../../lib/helpers/refactoringAdminLevelsAddRegistry';
import { ReactComponent as IconOpen } from '../../../images/icn-open.svg';
import Select from 'react-select';

const ProfileInfoPresentational = ({
  data = {},
  isUpdating = false,
  updateProfile,
  createProfile,
  deleteProfile,
  managers = [],
  activities = [],
  referringAgencies = [],
  softwares = [],
}) => {
  const [t] = useTranslation('CONTACTS');
  const alert = useAlert();
  const [{ newIsAdmin = false, newToken }] = useAuth();
  const storedFiscalCode = data.fiscalCode;

  const [deadlineCertificateDM140, setDeadlineCertificateDM140] = useState(
    data.deadlineCertificateDM140
  );
  const [isAgent, setIsAgent] = useState(false);
  const [isCommercial, setIsCommercial] = useState(data.commercial);
  const [isSubAgency, setIsSubAgency] = useState(data.isSubAgency);
  const [bracelet, setBracelet] = useState(data.bracelet || false);
  const [crm, setCrm] = useState(data.crm || false);
  const [isMaster, setIsMaster] = useState(false);
  const [studioManager, setStudioManager] = useState(data.studioManager || '');
  const [software, setSoftware] = useState(data.software || '');

  const [selectedActivities, setSelectedActivities] = useState(
    data.activities
      ? data.activities.map((activity) =>
          activity._id ? activity._id : activity
        )
      : []
  );
  const {
    register,
    errors,
    handleSubmit,
    watch,
    setValue,
    getValues,
    clearError,
  } = useForm({
    mode: 'onChange',
    defaultValues: data,
  });

  const region = watch('region');
  const userType = watch('type');

  const isFirstRender = React.useRef(true);

  useEffect(() => {
    setIsAgent(data.agent);
    setIsMaster(data.master);
    setIsCommercial(data.commercial);
    setIsSubAgency(data.isSubAgency || false);
    setBracelet(data.bracelet || false);
    setCrm(data.crm || false);
  }, [data]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setValue('fiscalCode', '');
    clearError('fiscalCode');
  }, [userType]);

  function onSubmit(values) {
    const formData = {
      ...values,
      agent: isAgent,
      master: isMaster,
      commercial: userType === 'COMMERCIAL' ? true : isCommercial,
      isSubAgency: isSubAgency,
      bracelet: bracelet,
      crm: crm,
      credits: ADMIN_TYPES.includes(userType) ? Number(values.credits) : 0,
      studioManager,
      software: software?._id || '',
      activities: selectedActivities,
      shareContract: [...AGENT_TYPES, 'COMMERCIAL'].includes(userType)
        ? Number(values.shareContract)
        : 0,
      invoicePercExtra: ['ASSOCIATION'].includes(userType)
        ? Number(values.invoicePercExtra)
        : 0,
      invoicePercAdmin: ['ASSOCIATION'].includes(userType)
        ? Number(values.invoicePercAdmin)
        : 0,
      officePerc: ['ASSOCIATION'].includes(userType)
        ? Number(values.officePerc)
        : 0,
      ...(deadlineCertificateDM140 && { deadlineCertificateDM140 }),
    };

    isUpdating ? updateProfile(formData) : createProfile(formData);
  }

  function getDistricts() {
    const currentRegion = isUpdating && !region ? data.region : region;
    const { capoluoghi = [] } =
      regions.find((r) => r.nome === currentRegion) || {};
    return capoluoghi;
  }

  function openUrl(url) {
    if (url.match(URL_REGEX)) return window.open(url, '_blank');

    alert.info(t('INVALID_URL'));
  }

  const options = activities.map((activity) => ({
    value: activity._id,
    label: activity.name,
  }));

  const handleChange = (selectedOptions) => {
    setSelectedActivities(selectedOptions.map((option) => option.value));
  };

  return (
    <section>
      <TabContent activeTab={'1'}>
        <TabPane tabId="1">
          <Row>
            {AdminTypes.includes(userType) && (
              <>
                <Col sm="12" md="6" lg="2">
                  <FormGroup>
                    <Label>{t('IS_COMMERCIAL')}</Label>
                    <div
                      onChange={({ target }) => {
                        setIsCommercial(target.value === '1');
                      }}
                    >
                      <CustomInput
                        type="radio"
                        id="commercialRadio1"
                        label="SI"
                        name="isCommercial"
                        value="1"
                        inline
                        checked={isCommercial}
                      />
                      <CustomInput
                        type="radio"
                        id="commercialRadio2"
                        label="NO"
                        name="isCommercial"
                        value="0"
                        inline
                        checked={!isCommercial}
                      />
                    </div>
                  </FormGroup>
                </Col>
              </>
            )}
            {[...AdminTypes, 'COMMERCIAL', 'ASSOCIATION'].includes(
              userType
            ) && (
              <Col sm="12" md="6" lg="2">
                <FormGroup>
                  <Label>{t('IS_SUB_AGENCY')}</Label>
                  <div
                    onChange={({ target }) => {
                      setIsSubAgency(target.value === '1');
                    }}
                  >
                    <CustomInput
                      type="radio"
                      id="subAgencyRadio1"
                      label="SI"
                      name="isSubAgency"
                      value="1"
                      inline
                      checked={isSubAgency}
                    />
                    <CustomInput
                      type="radio"
                      id="subAgencyRadio2"
                      label="NO"
                      name="isSubAgency"
                      value="0"
                      inline
                      checked={!isSubAgency}
                    />
                  </div>
                </FormGroup>
              </Col>
            )}
            <Col sm="12" md="6" lg="2">
              <FormGroup>
                <Label>{t('BRACELET')}</Label>
                <div
                  onChange={({ target }) => {
                    setBracelet(target.value === '1');
                  }}
                >
                  <CustomInput
                    type="radio"
                    id="braceletRadio1"
                    label="SI"
                    name="bracelet"
                    value="1"
                    inline
                    checked={bracelet}
                  />
                  <CustomInput
                    type="radio"
                    id="braceletRadio2"
                    label="NO"
                    name="bracelet"
                    value="0"
                    inline
                    checked={!bracelet}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col sm="12" md="6" lg="2">
              <FormGroup>
                <Label>{t('CRM')}</Label>
                <div
                  onChange={({ target }) => {
                    setCrm(target.value === '1');
                  }}
                >
                  <CustomInput
                    type="radio"
                    id="crmRadio1"
                    label="SI"
                    name="crm"
                    value="1"
                    inline
                    checked={crm}
                  />
                  <CustomInput
                    type="radio"
                    id="crmRadio2"
                    label="NO"
                    name="crm"
                    value="0"
                    inline
                    checked={!crm}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col sm="12" md="6" lg="3">
              <FormGroup>
                <Label>{t('CUSTOMER_TYPE')}</Label>
                <CustomInput
                  type="select"
                  id="exampleCustomSelect"
                  name="type"
                  innerRef={register({ required: t('COMMON:REQUIRED') })}
                  defaultValue={data.type}
                  disabled={!!data.type}
                >
                  {ProfileTypes.filter(
                    (elem) =>
                      !!data.type ||
                      ![
                        'BUILDING_ADMIN_2',
                        'BUILDING_ADMIN_3',
                        'BUILDING_ADMIN_4',
                        'BUILDING_ADMIN_5',
                      ].includes(elem)
                  ).map((type) => (
                    <option value={type} key={type}>
                      {t(refactoringAdminLevelsAddRegistry(type))}
                    </option>
                  ))}
                </CustomInput>
                <FormErrorMessage errors={errors} name="type" />
              </FormGroup>
            </Col>

            {AdminTypes.includes(userType) && (
              <Col sm="12" md="6" lg="3">
                <FormGroup>
                  <ContFilterCheckbox>
                    <CustomInput
                      type="checkbox"
                      id="isGoldCertified"
                      name="isGoldCertified"
                      label="Certificazione Gold"
                      inline
                      innerRef={register('isGoldCertified')}
                    />
                  </ContFilterCheckbox>
                </FormGroup>
                <FormGroup>
                  <ContFilterCheckbox>
                    <CustomInput
                      type="checkbox"
                      id="isPlatinumCertified"
                      name="isPlatinumCertified"
                      label="Certificazione Platino"
                      inline
                      innerRef={register('isPlatinumCertified')}
                    />
                  </ContFilterCheckbox>
                </FormGroup>
              </Col>
            )}
          </Row>
          <br />
          <Row>
            <Col sm="12" md="6" lg="6">
              <FormGroup>
                <Label>{t('BUSINESS_NAME')}</Label>
                <Input
                  type="text"
                  name="businessName"
                  defaultValue={data.businessName}
                  innerRef={register({
                    required: t('COMMON:REQUIRED'),
                    maxLength: 500,
                  })}
                />
                <FormErrorMessage errors={errors} name="businessName" />
              </FormGroup>
            </Col>
            <Col sm="12" md="6" lg="6">
              <FormGroup>
                <Label>{t('NAME')}</Label>
                <Input
                  type="text"
                  name="name"
                  maxLength="50"
                  defaultValue={data.name}
                  innerRef={register({
                    required: t('COMMON:REQUIRED'),
                  })}
                />
                <FormErrorMessage errors={errors} name="name" />
              </FormGroup>
            </Col>
            {!['CERTIFIER', 'LAWYER', 'MARKETING'].includes(userType) && (
              <>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('ADDRESS')} *</Label>
                    <Input
                      type="text"
                      name="address"
                      maxLength="100"
                      innerRef={register({
                        required: t('COMMON:REQUIRED'),
                      })}
                      defaultValue={data.address}
                    />
                    <FormErrorMessage errors={errors} name="address" />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="2">
                  <FormGroup>
                    <Label>{t('CITY')} *</Label>
                    <Input
                      type="text"
                      name="city"
                      innerRef={register({
                        required: t('COMMON:REQUIRED'),
                      })}
                      defaultValue={data.city}
                    />
                    <FormErrorMessage errors={errors} name="city" />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="2">
                  <FormGroup>
                    <Label>{t('REGION')} *</Label>
                    <CustomInput
                      id="regionSelect"
                      type="select"
                      name="region"
                      innerRef={register({
                        required: t('COMMON:REQUIRED'),
                      })}
                      defaultValue={data.region}
                      onChange={() => setValue('district', '')}
                    >
                      <option value="">-</option>
                      {regions.map((region, i) => (
                        <option key={i} value={region.nome}>
                          {region.nome}
                        </option>
                      ))}
                    </CustomInput>
                    <FormErrorMessage errors={errors} name="region" />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="2">
                  <FormGroup>
                    <Label>{t('PROVINCE')} *</Label>
                    <CustomInput
                      id="districtSelect"
                      type="select"
                      name="district"
                      defaultValue={data.district}
                      innerRef={register({
                        required: t('COMMON:REQUIRED'),
                      })}
                    >
                      <option value="">-</option>
                      {getDistricts().map((district, i) => (
                        <option key={i} value={district}>
                          {district}
                        </option>
                      ))}
                    </CustomInput>
                    <FormErrorMessage errors={errors} name="district" />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="2">
                  <FormGroup>
                    <Label>{t('CAP')} *</Label>
                    <Input
                      type="number"
                      name="cap"
                      innerRef={register({
                        required: t('COMMON:REQUIRED'),
                        pattern: {
                          value: POSTAL_CODE_REGEX,
                          message: t('COMMON:INVALID_POSTAL_CODE'),
                        },
                      })}
                      defaultValue={data.cap}
                    />
                    <FormErrorMessage errors={errors} name="cap" />
                  </FormGroup>
                </Col>
              </>
            )}
            <Col sm="12" md="6" lg="4">
              <FormGroup>
                <Label>{t('VAT')} *</Label>
                <Input
                  type="text"
                  name="taxCode"
                  maxLength="20"
                  innerRef={register({
                    required: t('COMMON:REQUIRED'),
                    pattern: {
                      value: VAT_CODE_REGEX,
                      message: t('COMMON:INVALID_TAX_CODE'),
                    },
                  })}
                  defaultValue={data.taxCode}
                />
                <FormErrorMessage errors={errors} name="taxCode" />
              </FormGroup>
            </Col>
            {!['CERTIFIER', 'LAWYER', 'MARKETING'].includes(userType) && (
              <>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('FISCAL_CODE')} *</Label>
                    <Input
                      type="text"
                      name="fiscalCode"
                      maxLength="20"
                      innerRef={register({
                        required: t('COMMON:REQUIRED'),
                        pattern: {
                          value: FISCAL_CODE_REGEX,
                          message: t('COMMON:INVALID_FISCAL_CODE'),
                        },
                        validate: {
                          checkFiscalCode: async (fiscalCode) => {
                            const {
                              data: { isDuplicated, consumerAlreadyExist },
                            } = await validateFiscalCode(
                              fiscalCode,
                              userType,
                              newToken
                            );

                            if (
                              isUpdating &&
                              storedFiscalCode.toLowerCase() ===
                                watch('fiscalCode').toLowerCase()
                            )
                              return true;

                            if (consumerAlreadyExist) {
                              return t('CONSUMER_ALREADY_EXIST');
                            }

                            return !isDuplicated || t('FISCAL_CODE_ERROR');
                          },
                        },
                      })}
                      defaultValue={data.fiscalCode}
                    />
                    <FormErrorMessage errors={errors} name="fiscalCode" />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('CELL')}</Label>
                    <Input
                      type="text"
                      name="cell"
                      maxLength="20"
                      innerRef={register}
                      defaultValue={data.cell}
                    />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('PHONE')}</Label>
                    <Input
                      type="text"
                      name="phone"
                      maxLength="20"
                      innerRef={register}
                      defaultValue={data.phone}
                    />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('EMAIL')}</Label>
                    <Input
                      type="email"
                      name="emailReference"
                      maxLength="320"
                      innerRef={register({
                        pattern: {
                          value: /\S+@\S+\.\S+/,
                          message: t('EMAIL_INVALID'),
                        },
                      })}
                      defaultValue={data.emailReference}
                    />
                    <FormErrorMessage errors={errors} name="emailReference" />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('PHONE_REFERENCE')}</Label>
                    <Input
                      type="text"
                      name="phoneReference"
                      maxLength="20"
                      innerRef={register}
                      defaultValue={data.phoneReference}
                    />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('EMAIL_BILLING')} *</Label>
                    <Input
                      type="email"
                      name="emailBilling"
                      maxLength="320"
                      innerRef={register({
                        required: t('COMMON:REQUIRED'),
                        pattern: {
                          value: /\S+@\S+\.\S+/,
                          message: t('EMAIL_INVALID'),
                        },
                      })}
                      defaultValue={data.emailBilling}
                    />
                    <FormErrorMessage errors={errors} name="emailBilling" />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('PHONE_BILLING')}</Label>
                    <Input
                      type="text"
                      name="phoneBilling"
                      maxLength="20"
                      innerRef={register}
                      defaultValue={data.phoneBilling}
                    />
                  </FormGroup>
                </Col>
              </>
            )}
            <Col sm="12" md="6" lg="4">
              <FormGroup>
                <Label>{t('EMAIL_COMUNICATION')} *</Label>
                <Input
                  type="email"
                  name="emailCommunication"
                  maxLength="320"
                  innerRef={register({
                    required: t('COMMON:REQUIRED'),
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: t('EMAIL_INVALID'),
                    },
                  })}
                  defaultValue={data.emailCommunication}
                />
                <FormErrorMessage errors={errors} name="emailCommunication" />
              </FormGroup>
            </Col>
            <Col sm="12" md="6" lg="4">
              <FormGroup>
                <Label>{t('PHONE_COMUNICATION')}</Label>
                <Input
                  type="text"
                  name="phoneCommunication"
                  maxLength="20"
                  innerRef={register}
                  defaultValue={data.phoneCommunication}
                />
              </FormGroup>
            </Col>
            {!['CERTIFIER', 'LAWYER', 'MARKETING'].includes(userType) && (
              <>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('EMAIL_PEC')}</Label>
                    <Input
                      type="email"
                      name="emailPec"
                      maxLength="320"
                      innerRef={register({
                        pattern: {
                          value: /\S+@\S+\.\S+/,
                          message: t('EMAIL_INVALID'),
                        },
                      })}
                      defaultValue={data.emailPec}
                    />
                    <FormErrorMessage errors={errors} name="emailPec" />
                  </FormGroup>
                </Col>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('SDI_CODE')} *</Label>
                    <Input
                      type="text"
                      name="sdiCode"
                      className="uppercase"
                      innerRef={register({
                        minLength: {
                          value: 7,
                          message: t('COMMON:INVALID_SDI_CODE'),
                        },
                        maxLength: {
                          value: 7,
                          message: t('COMMON:INVALID_SDI_CODE'),
                        },
                        required: t('COMMON:REQUIRED'),
                      })}
                      defaultValue={data.sdiCode}
                    />
                    <FormErrorMessage errors={errors} name="sdiCode" />
                  </FormGroup>
                </Col>
                {['BUILDING_ADMIN_1', 'BUILDING_ADMIN_2', 'BUILDING_ADMIN_3', 'BUILDING_ADMIN_4', 'BUILDING_ADMIN_5'].includes(userType) && (
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('CONDO_SDI_CODE')}</Label>
                    <Input
                      type="text"
                      name="condoSdiCode"
                      className="uppercase"
                      innerRef={register({
                        minLength: {
                          value: 7,
                          message: t('COMMON:INVALID_SDI_CODE'),
                        },
                        maxLength: {
                          value: 7,
                          message: t('COMMON:INVALID_SDI_CODE'),
                        },
                        // required: t('COMMON:REQUIRED'),
                      })}
                      defaultValue={data.condoSdiCode}
                    />
                    <FormErrorMessage errors={errors} name="condoSdiCode" />
                  </FormGroup>
                </Col>
                )}
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('ID_EXTRA')}</Label>
                    <Input
                      type="text"
                      name="gabettiId"
                      maxLength="50"
                      innerRef={register}
                      defaultValue={data.gabettiId}
                    />
                  </FormGroup>
                </Col>
              </>
            )}
            {['SUPPLIER'].includes(userType) && (
              <>
                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('CCIAA')}</Label>
                    <Input
                      type="text"
                      name="ccia"
                      innerRef={register}
                      defaultValue={data.ccia}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('LEGAL_REPRESENTATIVE')}</Label>
                    <Input
                      type="text"
                      name="legalRepresentative"
                      innerRef={register}
                      defaultValue={data.legalRepresentative}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="4">
                  <FormGroup>
                    <Label>{t('CF_LEGAL_REPRESENTATIVE')}</Label>
                    <Input
                      type="text"
                      name="cfLegalRepresentative"
                      maxLength="20"
                      innerRef={register({
                        pattern: {
                          value: FISCAL_CODE_REGEX,
                          message: t('COMMON:INVALID_FISCAL_CODE'),
                        },
                      })}
                      defaultValue={data.cfLegalRepresentative}
                    />
                    <FormErrorMessage
                      errors={errors}
                      name="cfLegalRepresentative"
                    />
                  </FormGroup>
                </Col>
              </>
            )}
            <Col sm="12" md="6" lg="4">
              <FormGroup>
                <Label>{t('STUDIO_MANAGER')}</Label>
                <CustomInput
                  id="studioManager"
                  type="select"
                  name="studioManager"
                  value={studioManager}
                  onChange={(e) => {
                    setStudioManager(e.target.value);
                  }}
                >
                  <option value="">-</option>
                  {managers.map((manager, i) => (
                    <option key={i} value={manager._id}>
                      {manager.name}
                    </option>
                  ))}
                </CustomInput>
                <FormErrorMessage errors={errors} name="studioManager" />
              </FormGroup>
            </Col>
            <Col sm="12" md="6" lg="4">
              <FormGroup>
                <Label>{t('SOFTWARE')}</Label>
                <CustomInput
                  id="software"
                  type="select"
                  name="software"
                  value={software?._id}
                  onChange={(e) => {
                    setSoftware({
                      ...software,
                      _id: e.target.value,
                    });
                  }}
                >
                  <option value="">-</option>
                  {softwares.map((elem) => (
                    <option value={elem._id} key={elem._id}>
                      {elem.name}
                    </option>
                  ))}
                </CustomInput>
                <FormErrorMessage errors={errors} name="software" />
              </FormGroup>
            </Col>
            {['SUPPLIER_INS'].includes(userType) && (
              <Col sm="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('AGENCY_TYPE')} *</Label>
                  <CustomInput
                    id="agencyType"
                    type="select"
                    name="agencyType"
                    defaultValue={data.agencyType}
                    innerRef={register({
                      required: t('COMMON:REQUIRED'),
                    })}
                    onChange={(e) => setValue('agencyType', e.target.value)}
                  >
                    <option value="">-</option>
                    {referringAgencies.map((agency, i) => (
                      <option key={i} value={agency.name}>
                        {agency.name}
                      </option>
                    ))}
                  </CustomInput>
                  <FormErrorMessage errors={errors} name="agencyType" />
                </FormGroup>
              </Col>
            )}
            {['SUPPLIER', ...AdminTypes, 'AGENCY'].includes(userType) && (
              <Col sm="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('ACTIVITIES')}</Label>
                  <Select
                    id="activities"
                    name="activities"
                    isMulti
                    value={options.filter((option) =>
                      selectedActivities.includes(option.value)
                    )}
                    onChange={handleChange}
                    options={options}
                    placeholder={t('SELECT_ACTIVITIES')}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                  <FormErrorMessage errors={errors} name="activities" />
                </FormGroup>
              </Col>
            )}
          </Row>
          {!['CERTIFIER', 'LAWYER', 'MARKETING'].includes(userType) && (
            <Row>
              <Col sm="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('ID_AFFILIATE')}</Label>
                  <Input
                    type="text"
                    name="affiliationId"
                    maxLength="50"
                    innerRef={register}
                    defaultValue={data.affiliationId}
                  />
                </FormGroup>
              </Col>

              {ADMIN_TYPES.includes(userType) && (
                <>
                  <Col sm="12" md="6" lg="4">
                    <FormGroup>
                      <Label>{t('CREDITS')}</Label>
                      <Input
                        type="number"
                        name="credits"
                        maxLength="50"
                        innerRef={register}
                        defaultValue={data.credits}
                      />
                    </FormGroup>
                  </Col>
                </>
              )}
              {[...AGENT_TYPES, 'COMMERCIAL', 'ASSOCIATION'].includes(
                userType
              ) && (
                <>
                  <Col sm="12">
                    <H5Styled>{t('PERC_FEES')}</H5Styled>
                  </Col>
                  <Col sm="12" md="6" lg="4">
                    <FormGroup>
                      <Label>{t('SHARE_CONTRACT')}</Label>
                      <Input
                        type="number"
                        name="shareContract"
                        innerRef={register({
                          min: {
                            value: 0,
                            message: t('COMMON:MIN_SHARE_CONTRACT_PERCENTAGE'),
                          },
                          max: {
                            value: 100,
                            message: t('COMMON:MAX_SHARE_CONTRACT_PERCENTAGE'),
                          },
                        })}
                        defaultValue={data.shareContract}
                      />
                      <FormErrorMessage errors={errors} name="shareContract" />
                    </FormGroup>
                  </Col>
                </>
              )}
              {['ASSOCIATION'].includes(userType) && (
                <>
                  <Col sm="12" md="6" lg="4">
                    <FormGroup>
                      <Label>{t('INVOICE_PERC_EXTRA')}</Label>
                      <Input
                        type="number"
                        name="invoicePercExtra"
                        innerRef={register({
                          min: {
                            value: 0,
                            message: t('COMMON:MIN_SHARE_CONTRACT_PERCENTAGE'),
                          },
                          max: {
                            value: 100,
                            message: t('COMMON:MAX_SHARE_CONTRACT_PERCENTAGE'),
                          },
                        })}
                        defaultValue={data.invoicePercExtra}
                      />
                      <FormErrorMessage
                        errors={errors}
                        name="invoicePercExtra"
                      />
                    </FormGroup>
                  </Col>

                  <Col sm="12" md="6" lg="4">
                    <FormGroup>
                      <Label>{t('INVOICE_PERC_ADMIN')}</Label>
                      <Input
                        type="number"
                        name="invoicePercAdmin"
                        innerRef={register({
                          min: {
                            value: 0,
                            message: t('COMMON:MIN_SHARE_CONTRACT_PERCENTAGE'),
                          },
                          max: {
                            value: 100,
                            message: t('COMMON:MAX_SHARE_CONTRACT_PERCENTAGE'),
                          },
                        })}
                        defaultValue={data.invoicePercAdmin}
                      />
                      <FormErrorMessage
                        errors={errors}
                        name="invoicePercAdmin"
                      />
                    </FormGroup>
                  </Col>

                  <Col sm="12" md="6" lg="4">
                    <FormGroup>
                      <Label>{t('OFFICE_PERC')}</Label>
                      <Input
                        type="number"
                        name="officePerc"
                        innerRef={register({
                          min: {
                            value: 0,
                            message: t('COMMON:MIN_SHARE_CONTRACT_PERCENTAGE'),
                          },
                          max: {
                            value: 100,
                            message: t('COMMON:MAX_SHARE_CONTRACT_PERCENTAGE'),
                          },
                        })}
                        defaultValue={data.officePerc}
                      />
                      <FormErrorMessage errors={errors} name="officePerc" />
                    </FormGroup>
                  </Col>
                </>
              )}
            </Row>
          )}
          {newIsAdmin &&
            !['CERTIFIER', 'LAWYER', 'MARKETING'].includes(userType) && (
              <Row>
                <Col sm="12">
                  <FormGroup>
                    <Label>{t('OPERATIVE_NOTES')}</Label>
                    <Input
                      rows={6}
                      type="textarea"
                      name="notes"
                      innerRef={register({
                        maxLength: {
                          value: 2000,
                          message: t('OPERATIVE_NOTES_MAX_ERROR'),
                        },
                      })}
                      defaultValue={data.notes}
                    />
                    <FormErrorMessage errors={errors} name="notes" />
                  </FormGroup>
                </Col>
              </Row>
            )}
          {!['CERTIFIER', 'LAWYER', 'MARKETING'].includes(userType) && (
            <Row className="link-cont">
              <Col sm="12" md="12" lg="6">
                <H5Styled>{t('AREA_INTERVENTION')}</H5Styled>
                <InputLink>
                  <FormGroup>
                    <Label>{t('LINK')}</Label>
                    <Input
                      type="url"
                      name="interventionArea"
                      innerRef={register}
                      defaultValue={data.interventionArea}
                    />
                  </FormGroup>
                  <BtnRadiusRed
                    type="button"
                    title="Apri"
                    onClick={() => openUrl(data.interventionArea)}
                  >
                    <IconOpen />
                  </BtnRadiusRed>
                </InputLink>
              </Col>
              <Col sm="12" md="12" lg="6">
                <H5Styled>{t('TYPE_INTERVENTION')}</H5Styled>
                <InputLink>
                  <FormGroup>
                    <Label>{t('LINK')}</Label>
                    <Input
                      type="url"
                      name="interventionType"
                      innerRef={register}
                      defaultValue={data.interventionType}
                    />
                  </FormGroup>
                  <BtnRadiusRed
                    type="button"
                    title="Apri"
                    onClick={() => openUrl(data.interventionType)}
                  >
                    <IconOpen />
                  </BtnRadiusRed>
                </InputLink>
              </Col>
            </Row>
          )}
        </TabPane>
      </TabContent>

      <br />

      <ContFlexRight>
        {isUpdating ? (
          <React.Fragment>
            <ModalConfirm onConfirm={deleteProfile} text={t('DELETE_REQUEST')}>
              <ButtonDelete type="button">{t('BTN_DELETE')}</ButtonDelete>
            </ModalConfirm>
            <ButtonSave
              type="button"
              onClick={(e) => {
                if (Object.keys(errors).length > 0) window.scrollTo({ top: 0 });
                handleSubmit(onSubmit)(e);
              }}
            >
              {t('BTN_SAVE')}
            </ButtonSave>
          </React.Fragment>
        ) : (
          <ButtonSave
            type="button"
            onClick={(e) => {
              if (Object.keys(errors).length > 0) window.scrollTo({ top: 0 });
              handleSubmit(onSubmit)(e);
            }}
          >
            {t('BTN_CREATE')}
          </ButtonSave>
        )}
      </ContFlexRight>
    </section>
  );
};

export default ProfileInfoPresentational;
